import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import Select from 'components/global/CUK/AsyncSelect/Select'
import { OptionSingle } from 'components/global/CUK/AsyncSelect/components/Option'

export type SingleFilterOption<T> = {
  id: string
  label: string
  value: T
}

type SelectOption<T> = SingleFilterOption<T | null> & {
  selectedLabel: string
}

type SingleFilterSelectProps<T> = {
  placeholder: string
  options: SingleFilterOption<T>[]
  value: T | null
  onChange: (value: T | null) => void
}

const OptionWithLabel = props => <OptionSingle {...props} label={props.data.label} />

const SingleFilterSelect = <T,>({ placeholder, options, value, onChange }: SingleFilterSelectProps<T>) => {
  const { t } = useTranslation()

  const selectOptions = useMemo<SelectOption<T>[]>(() => [
    {
      id: 'all',
      label: t('admin_sites.filters.all'),
      value: null,
      selectedLabel: placeholder
    },
    ...options.map(option => ({
      ...option,
      selectedLabel: `${placeholder} : ${option.label}`
    }))
  ], [options, placeholder, t])

  const selectedOption = value === null
    ? null
    : selectOptions.find(option => option.value === value) ?? null

  const handleChange = (option: SelectOption<T> | Record<string, never> | null) => {
    if (!option || !('id' in option)) {
      onChange(null)
      return
    }
    onChange(option.value)
  }

  return (
    <Select
      /* @ts-expect-error JSX */
      options={selectOptions}
      mainField="selectedLabel"
      showSelectedValue={false}
      placeholder={placeholder}
      value={selectedOption}
      onChange={handleChange}
      aria-label={placeholder}
      components={{ Option: OptionWithLabel }}
    />
  )
}

export default SingleFilterSelect
